import { Box, Button, Popover, Stack, ToggleButton, ToggleButtonGroup, Typography, PopoverProps } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { addDays, addMonths, addYears } from "date-fns";


export enum PopTimeRangeKind {
  Custom = 0,
  Hours_24 = 1,
  Days_7 = 2,
  Days_30 = 3,
  Months_3 = 4,
  Months_6 = 5,
  Years_1 = 6,
}

export interface PopTimeRangePickerState {
  open: boolean;
  kind: PopTimeRangeKind;
  start: Date|null;
  end: Date|null;
}

export interface PopTimeRangePickerProps {
  states: PopTimeRangePickerState,
  PopTimeRangePicker: JSX.Element;
  open: () => void;
  close: (saveChange?: boolean) => void;
}

const getStartEndFromKind = (k:PopTimeRangeKind) => {
  const now = new Date()
  return (
      k === PopTimeRangeKind.Hours_24 ? { start: addDays(now, -1), end: null }
    : k === PopTimeRangeKind.Days_7   ? { start: addDays(now, -7), end: null }
    : k === PopTimeRangeKind.Days_30  ? { start: addDays(now, -30), end: null }
    : k === PopTimeRangeKind.Months_3 ? { start: addMonths(now, -3), end: null }
    : k === PopTimeRangeKind.Months_6 ? { start: addMonths(now, -6), end: null }
    : k === PopTimeRangeKind.Years_1  ? { start: addYears(now, -1), end: null }
    : null // value === PopTimeRangeKind.Custom   
  ) as {start: Date|null; end: Date|null;}|null
}

export const usePopTimeRangePicker = ({ label: labelProp, defaultKind, onChange, PopoverProps, excludesOptions = [], custom }: {
  label: string,
  defaultKind?: PopTimeRangeKind,
  onChange?: (x: PopTimeRangePickerState)=>void,
  PopoverProps?: Partial<PopoverProps>,
  excludesOptions?: PopTimeRangeKind[],
  custom?: { startBoundary?: Date|null, endBoundary?: Date|null },
}): PopTimeRangePickerProps => {
  const label = labelProp ?? 'Time Range'
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [states, setStates] = useState(() => {
    const kind = defaultKind != null ? defaultKind : PopTimeRangeKind.Hours_24
    const startEnd = getStartEndFromKind(kind)
    const initS = {
      staged: {
        open: false,
        kind,
        start: null as Date|null,
        end: null as Date|null,
        ...(startEnd),
      },
      temp: {
        kind,
        start: null as Date|null,
        end: null as Date|null,
        ...(startEnd),
      },
    }
    if (onChange) {
      onChange(initS.staged)
    }
    return initS
  });

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const {open, close} = useMemo(() => ({
    open: () => {
      setStates(v => ({...v, staged:{...v.staged, open: true}}))
    },
    close: (saveChange?: boolean) => {
      setStates(v => {
        const v1 = ({...v, staged:{...v.staged, 
          open: false
        }})
        if (v1.temp.kind === PopTimeRangeKind.Custom && saveChange) {
          if ((custom?.startBoundary && v1.temp.start && v1.temp.start < custom.startBoundary)
           || (custom?.endBoundary && v1.temp.end && v1.temp.end > custom.endBoundary)
          ) {
            v1.temp.start = v1.staged.start
            v1.temp.end = v1.staged.end
            v1.temp.kind = v1.staged.kind
          } else {
            v1.staged.start = v1.temp.start
            v1.staged.end = v1.temp.end
            v1.staged.kind = PopTimeRangeKind.Custom
            setTimeout(() => {
              if (onChangeRef.current) {
                onChangeRef.current(v1.staged)
              }
            })
          }
        } else {
          v1.temp.kind = v1.staged.kind
        }
        return v1
      })
    }
  }), [custom?.endBoundary, custom?.startBoundary])

  const handleKindChange = (e:any, value:PopTimeRangeKind) => {
    setStates(v => {
      if (value == null || v.temp.kind === value) { return v }
      else {
        const v1 = {...v, staged: {...v.staged,
          ...(getStartEndFromKind(value) as any),
        }}
        v1.temp.kind = value
        if (value !== PopTimeRangeKind.Custom) {
          v1.temp.start = v1.staged.start
          v1.temp.end = v1.staged.end
          v1.staged.open = false
          v1.staged.kind = value
          if (onChange) {
            setTimeout(() => onChange(v1.staged))
          }
        }
        return v1
      }
    })
  }

  const PopTimeRangePicker = (
    <>
      <Button
        color="inherit"
        endIcon={<ExpandMoreIcon fontSize="small" />}
        onClick={open}
        ref={anchorRef}
        //{...other}
      >
        {label}
      </Button>
      {states.staged.open &&
        <Popover
          anchorEl={anchorRef.current}
          open={states.staged.open}
          onClose={() => close()}
          PaperProps={{ sx: {width: 520, p: 2} }}
          {...PopoverProps}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {label}{" "}
              <Typography variant="caption">within:</Typography>
            </Typography>
            <ToggleButtonGroup
              value={states.temp.kind}
              exclusive
              onChange={handleKindChange}
              aria-label="predefined ranges"
              size="small"
              color="primary"
              sx={{ '& button': { 
                textTransform: 'lowercase', 
                px: 1.5, 
                color: theme => theme.palette.text.secondary,
              }}}
            >
              {excludesOptions.indexOf(PopTimeRangeKind.Hours_24) === -1 &&
                <ToggleButton value={PopTimeRangeKind.Hours_24} aria-label="24 hours">
                  <Typography variant="caption">
                    24 hours
                  </Typography>
                </ToggleButton>}
              {excludesOptions.indexOf(PopTimeRangeKind.Days_7) === -1 &&
                <ToggleButton value={PopTimeRangeKind.Days_7} aria-label="7 days">
                  <Typography variant="caption">
                    7 days
                  </Typography>
                </ToggleButton>}
              {excludesOptions.indexOf(PopTimeRangeKind.Days_30) === -1 &&
                <ToggleButton value={PopTimeRangeKind.Days_30} aria-label="30 days">
                  <Typography variant="caption">
                    30 days
                  </Typography>
                </ToggleButton>}
              {excludesOptions.indexOf(PopTimeRangeKind.Months_3) === -1 &&
                <ToggleButton value={PopTimeRangeKind.Months_3} aria-label="3 months">
                  <Typography variant="caption">
                    3 months
                  </Typography>
                </ToggleButton>}
              {excludesOptions.indexOf(PopTimeRangeKind.Months_6) === -1 &&
                <ToggleButton value={PopTimeRangeKind.Months_6} aria-label="6 months">
                  <Typography variant="caption">
                    6 months
                  </Typography>
                </ToggleButton>}
              {excludesOptions.indexOf(PopTimeRangeKind.Years_1) === -1 &&
                <ToggleButton value={PopTimeRangeKind.Years_1} aria-label="1 year">
                  <Typography variant="caption">
                    1 year
                  </Typography>
                </ToggleButton>}
              {excludesOptions.indexOf(PopTimeRangeKind.Custom) === -1 &&
                <ToggleButton value={PopTimeRangeKind.Custom} aria-label="custom range">
                  <Typography variant="caption">
                    custom range...
                  </Typography>
                </ToggleButton>}
            </ToggleButtonGroup>
          </Box>
          {states.temp.kind === PopTimeRangeKind.Custom &&
            <>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} >
                  <DateTimePicker 
                    label="Start"
                    ampm={false}
                    minDateTime={custom?.startBoundary}
                    maxDateTime={states.temp.end}
                    value={states.temp.start}
                    onChange={(newValue) => setStates(v => ({...v, temp:{...v.temp, start: newValue}}))}
                  />
                  <Typography>-</Typography>
                  <DateTimePicker 
                    label="End"
                    ampm={false}
                    maxDateTime={custom?.endBoundary}
                    minDateTime={states.temp.start as any}
                    value={states.temp.end}
                    onChange={(newValue) => setStates(v => ({...v, temp:{...v.temp, end: newValue}}))}
                  />
                </LocalizationProvider>
              </Stack>
              <Button variant="outlined" size="small" sx={{ float: 'right', mt: 2 }}
                onClick={() => close(true)}
              >
                Ok
              </Button>
            </>
            }
        </Popover>}
    </>
  )
  return {
    states: states.staged,
    PopTimeRangePicker,
    open,
    close,
  }
}

export const displayTimeRangeLabel = (k: PopTimeRangeKind) => {
  switch (k) {
    case PopTimeRangeKind.Custom: return "custom range";
    case PopTimeRangeKind.Hours_24: return "24 hours";
    case PopTimeRangeKind.Days_7: return "7 days";
    case PopTimeRangeKind.Days_30: return "30 days";
    case PopTimeRangeKind.Months_3: return "3 months";
    case PopTimeRangeKind.Months_6: return "6 months";
    case PopTimeRangeKind.Years_1: return "1 year";
    default: break;
  }
}
